/**
 * Father's Day coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const fathersDayColoringPagesPack: ToilePack = {
    slug: 'fathers-day-coloring-pages',
    category: 'season',
    name: "Father's Day coloring pages",
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'tie', label: 'Shirt and tie', subject: 'a shirt collar with a striped necktie filling the page and "Happy Father\'s Day" in bubble letters above it'},
        {key: 'tools', label: 'Toolbox', subject: 'an open toolbox with a hammer, a wrench and a screwdriver standing in it, the words "Dad Can Fix Anything" on its side'},
        {key: 'fishing', label: 'Fishing trip', subject: 'a rowing boat on a lake with a fishing rod bent over a jumping fish, pine trees behind and a banner reading "Best Dad Ever"'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and easy', style: 'thick black outlines of even weight, large simple shapes and wide open spaces for small hands and crayons', palette: 'black ink only', framing: 'The drawing centered on the page with a wide white margin'},
        {key: 'doodle', label: 'Doodle fill', style: 'medium black outlines with the shapes and letters filled with small doodle patterns of dots, stripes, hearts and tiny flowers to color slowly', palette: 'black ink only', framing: 'The drawing filling the page with a thin white margin'},
        {key: 'card', label: 'Card front', style: 'clean medium black outlines laid out as the front of a greeting card', palette: 'black ink only', framing: 'The drawing set inside a scalloped double-line border like a card front, a white margin around the border'},
    ],
    directions: ['for grandpa', 'barbecue', 'Love you Papa'],
    directionHint: 'try: for grandpa',
    seo: {
        title: "Father's Day Coloring Pages: Tie, Toolbox, Fishing",
        metaDescription: "Printable Father's Day coloring pages: a shirt and tie, a toolbox and a fishing trip, in three line styles for kids.",
        h1: "Father's Day coloring pages: shirt and tie, toolbox, fishing trip",
        tagline: 'Nine outline drawings with a greeting lettered large, in three styles: bold and easy, doodle fill, card front.',
        intro: "Printable coloring pages a child colors and gives to their father, on the third Sunday of June in many countries. Each is an outline drawing with a large greeting: a striped tie under Happy Father's Day, an open toolbox with Dad Can Fix Anything on its side, or a rowing boat with a fish on the line. Bold and easy, doodle fill or card front. Type a different subject, such as for grandpa, and the same styles carry it.",
        useCases: [
            "a card a child colors for Father's Day",
            "a classroom craft in the week before Father's Day",
            'a quiet activity on a holiday morning',
            'a page to color for a grandfather',
        ],
        faq: [
            {q: "Do these match the Mother's Day coloring pages?", a: 'They use the same three styles, with a different set of subjects for Dad.'},
            {q: 'What is the doodle fill style?', a: 'Medium black outlines with the shapes and letters filled with small patterns of dots, stripes, hearts and tiny flowers, to color slowly.'},
            {q: 'Are the pages colored in?', a: 'No, they are black outlines on plain white, made for crayons or markers.'},
        ],
    },
}
